import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, ArrowRight, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function DevicesList() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/devices");
      setProjects(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    await api.delete(`/devices/${id}`);
    toast.success("Project deleted");
    load();
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="vq-overline mb-2">Projects</div>
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900">Device pipeline</h1>
          </div>
          <Link to="/devices/new" data-testid="devices-new-btn">
            <Button className="h-11 bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
              <Plus className="w-4 h-4 mr-2" /> New project
            </Button>
          </Link>
        </div>

        <div className="vq-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left px-6 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Device</th>
                <th className="text-left px-6 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Type</th>
                <th className="text-left px-6 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Markets</th>
                <th className="text-left px-6 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Status</th>
                <th className="text-right px-6 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="text-center py-12 text-slate-500">Loading…</td></tr>
              ) : projects.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-16">
                  <p className="text-slate-500 mb-4">No projects yet.</p>
                  <Link to="/devices/new"><Button className="bg-slate-900 hover:bg-[#0055FF] text-white">Create your first project</Button></Link>
                </td></tr>
              ) : projects.map((p) => (
                <tr key={p.project_id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <Link to={`/devices/${p.project_id}`} className="block">
                      <div className="font-display font-bold text-slate-900">{p.device_name}</div>
                      <div className="text-xs text-slate-500 font-mono">{p.project_id}</div>
                    </Link>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-700">{p.device_type}</td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {p.markets?.map((m) => (
                        <span key={m} className="px-1.5 py-0.5 text-[10px] font-mono border border-slate-200 rounded text-slate-600">{m}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <StatusPill status={p.status} />
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Link to={`/devices/${p.project_id}`} data-testid={`open-${p.project_id}`}>
                        <Button variant="ghost" size="sm"><ArrowRight className="w-4 h-4" /></Button>
                      </Link>
                      <Button variant="ghost" size="sm" onClick={() => remove(p.project_id)} data-testid={`delete-${p.project_id}`}>
                        <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}

function StatusPill({ status }) {
  const map = {
    draft: { label: "Draft", cls: "bg-slate-100 text-slate-700 border-slate-200" },
    classified: { label: "Classified", cls: "bg-blue-50 text-blue-700 border-blue-200" },
    qms_generated: { label: "QMS Ready", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  };
  const s = map[status] || map.draft;
  return <span className={`px-2 py-1 rounded text-[10px] font-mono border ${s.cls}`}>{s.label}</span>;
}
